import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Shield, ChevronDown, Globe, User } from "lucide-react";
import { LIVE_STATS } from "../mock";
import { useServerStatus, useServerStats } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { useContentList, useContentObject } from "../hooks/useContentData";
import { contentFallback } from "../contentDefaults";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("ES");
  const location = useLocation();
  const { data: status } = useServerStatus();
  const { data: stats } = useServerStats();
  const { user } = useAuth();
  const { items: navItems } = useContentList("nav.items", contentFallback("nav.items"));
  const { data: brand } = useContentObject("layout.brand", contentFallback("layout.brand"));
  const { data: topbar } = useContentObject("layout.topbar", contentFallback("layout.topbar"));
  const { data: theme } = useContentObject("theme.branding", contentFallback("theme.branding"));
  const LINKS = navItems.length > 0 ? navItems : contentFallback("nav.items");

  const isOnline = status ? status.gameserver === "online" : true;
  const onlineCount = stats?.online ?? LIVE_STATS.online;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0a0a0d]/85 backdrop-blur-xl border-b border-[rgba(212,168,67,0.15)]" : "bg-transparent"
      }`}
    >
      <div className="hidden md:flex items-center justify-between text-[11px] tracking-[0.2em] uppercase font-mono text-[#8a8a92] px-8 py-1.5 border-b border-[rgba(212,168,67,0.08)]">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              {isOnline && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${isOnline ? "bg-emerald-400" : "bg-red-500"}`} />
            </span>
            Server {isOnline ? "Online" : "Offline"}
          </span>
          <span className="text-[#6f6c66]">|</span>
          <span className="text-[#d4a843]">{onlineCount.toLocaleString()} jugadores online</span>
          {topbar?.rates_line && (
            <>
              <span className="text-[#6f6c66]">|</span>
              <span>{topbar.rates_line}</span>
            </>
          )}
        </div>
        <button
          onClick={() => setLang(lang === "ES" ? "EN" : "ES")}
          className="flex items-center gap-1.5 hover:text-[#d4a843] transition-colors"
        >
          <Globe className="w-3 h-3" /> {lang}
          <ChevronDown className="w-3 h-3" />
        </button>
      </div>

      <nav className="flex items-center justify-between px-6 md:px-10 py-4">
        <Link to="/" className="group flex items-center gap-3">
          <div className="relative w-10 h-10 shrink-0">
            {theme?.logo_url ? (
              <img src={theme.logo_url} alt="" className="w-10 h-10 rounded-full object-cover border border-[rgba(212,168,67,0.35)]" />
            ) : (
              <>
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--gold-light,#f5c542)] via-[var(--gold,#d4a843)] to-[var(--gold-dark,#8b6914)] opacity-90 group-hover:opacity-100 transition" />
                <div className="absolute inset-[3px] rounded-full bg-[#0a0a0d] flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[var(--gold,#d4a843)]" />
                </div>
              </>
            )}
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-extrabold text-lg tracking-[0.18em] text-[#f0ebe0]">
              {brand?.name || "L2 AETHERIS"}
            </span>
            <span className="font-mono text-[9px] tracking-[0.4em] text-[#d4a843] uppercase">
              {brand?.tagline || "Interlude · Premium"}
            </span>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => {
            const active = location.pathname === l.url;
            const inner = (
              <>
                {l.label}
                {active && <span className="absolute left-1/2 -translate-x-1/2 bottom-0 w-6 h-px bg-[#d4a843]" />}
              </>
            );
            const className = `relative px-5 py-2 font-display text-sm tracking-[0.2em] uppercase transition-colors ${
              active ? "text-[#d4a843]" : "text-[#b8b3a8] hover:text-[#f0ebe0]"
            }`;
            return l.external ? (
              <a key={l.url + l.label} href={l.url} target="_blank" rel="noreferrer" className={className}>{inner}</a>
            ) : (
              <Link key={l.url + l.label} to={l.url} className={className}>{inner}</Link>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {user?.is_admin && (
            <Link to="/admin" className="btn-ghost px-3 py-2.5 text-xs rounded-sm flex items-center gap-2 border border-[#d4a843]/30 text-[#d4a843]" data-testid="nav-admin">
              Admin
            </Link>
          )}
          {user ? (
            <Link to="/account/panel" className="btn-ghost px-4 py-2.5 text-xs rounded-sm flex items-center gap-2">
              <User className="w-3.5 h-3.5" /> {user.account}
            </Link>
          ) : (
            <Link to="/account" className="btn-ghost px-4 py-2.5 text-xs rounded-sm flex items-center gap-2">
              <User className="w-3.5 h-3.5" /> Mi Cuenta
            </Link>
          )}
          <Link to="/downloads" className="btn-gold px-6 py-2.5 text-xs rounded-sm">
            Descargar
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#f0ebe0] hover:text-[#d4a843] transition-colors"
          aria-label="menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-[#0a0a0d]/98 backdrop-blur-xl border-t border-[rgba(212,168,67,0.15)] animate-fade-in">
          <div className="flex flex-col px-6 py-4 gap-1">
            {LINKS.map((l) => l.external ? (
              <a key={l.url + l.label} href={l.url} target="_blank" rel="noreferrer"
                className="py-3 font-display text-sm tracking-[0.2em] uppercase text-[#f0ebe0] hover:text-[#d4a843] border-b border-[rgba(212,168,67,0.08)]">
                {l.label}
              </a>
            ) : (
              <Link key={l.url + l.label} to={l.url}
                className="py-3 font-display text-sm tracking-[0.2em] uppercase text-[#f0ebe0] hover:text-[#d4a843] border-b border-[rgba(212,168,67,0.08)]">
                {l.label}
              </Link>
            ))}
            <Link to="/downloads" className="btn-gold mt-4 px-6 py-3 text-xs rounded-sm text-center">
              Descargar Cliente
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
